import React, { useState, useEffect } from "react";
import { useAuth, apiService } from "../services/api";
import Layout from "../components/Layout";
import {
  Database,
  Plus,
  MoreVertical,
  Trash2,
  Edit,
  TestTube,
  Eye,
  Loader2,
  CheckCircle2,
  XCircle,
  FileText,
  Copy,
  Settings2,
  Table,
  Search,
  Check,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import UsersManagementTab from "@/components/UsersManagementTab";

const SettingsPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState("connectors");

  // Connectors state
  const [connectors, setConnectors] = useState([]);
  const [loadingConnectors, setLoadingConnectors] = useState(true);
  const [connectorDialogOpen, setConnectorDialogOpen] = useState(false);
  const [editingConnector, setEditingConnector] = useState(null);
  const [connectorForm, setConnectorForm] = useState({
    name: "",
    host: "",
    port: 3050,
    database_path: "",
    charset: "UTF8",
    username: "",
    password: "",
  });
  const [savingConnector, setSavingConnector] = useState(false);
  const [testingConnector, setTestingConnector] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [deleteConnectorOpen, setDeleteConnectorOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);

  // Schema dialog
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [schemaData, setSchemaData] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);

  // Tables selection dialog
  const [tablesDialogOpen, setTablesDialogOpen] = useState(false);
  const [allTables, setAllTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [savingTables, setSavingTables] = useState(false);
  const [tableSearchTerm, setTableSearchTerm] = useState("");

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applyingTemplate, setApplyingTemplate] = useState(null);

  const fetchConnectors = async () => {
    try {
      const response = await apiService.getConnectors(token);
      setConnectors(response.data);
    } catch (error) {
      toast.error("Erro ao carregar conectores");
    } finally {
      setLoadingConnectors(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiService.getTemplates(null, token);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Erro ao carregar templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchConnectors();
    fetchTemplates();
  }, [token]);

  // Connector handlers
  const handleOpenConnectorDialog = (connector = null) => {
    if (connector) {
      setEditingConnector(connector);
      setConnectorForm({
        name: connector.name,
        host: connector.host,
        port: connector.port,
        database_path: connector.database_path,
        charset: connector.charset,
        username: connector.username,
        password: "",
      });
    } else {
      setEditingConnector(null);
      setConnectorForm({
        name: "",
        host: "",
        port: 3050,
        database_path: "",
        charset: "UTF8",
        username: "",
        password: "",
      });
    }
    setTestResult(null);
    setConnectorDialogOpen(true);
  };

  const handleSaveConnector = async () => {
    if (
      !connectorForm.name ||
      !connectorForm.host ||
      !connectorForm.database_path ||
      !connectorForm.username
    ) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (!editingConnector && !connectorForm.password) {
      toast.error("Senha é obrigatória para novo conector");
      return;
    }

    setSavingConnector(true);
    try {
      if (editingConnector) {
        await apiService.updateConnector(
          editingConnector.id,
          connectorForm,
          token,
        );
        toast.success("Conector atualizado!");
      } else {
        await apiService.createConnector(connectorForm, token);
        toast.success("Conector criado!");
      }
      setConnectorDialogOpen(false);
      fetchConnectors();
    } catch (error) {
      toast.error("Erro ao salvar conector");
    } finally {
      setSavingConnector(false);
    }
  };

  const handleTestConnector = async (connectorId) => {
    setTestingConnector(connectorId);
    setTestResult(null);
    try {
      const response = await apiService.testConnector(connectorId, token);

      if (response.data.success) {
        setTestResult({
          id: connectorId,
          success: true,
          message: response.data.message,
        });
        toast.success("Conexão bem-sucedida!");
      } else {
        setTestResult({
          id: connectorId,
          success: false,
          message: response.data.message,
        });
        toast.error(`Falha: ${response.data.message}`);
      }
    } catch (error) {
      setTestResult({
        id: connectorId,
        success: false,
        message: "Erro ao comunicar com o servidor",
      });
      toast.error("Falha na requisição");
    } finally {
      setTestingConnector(null);
    }
  };

  const handleDeleteConnector = async () => {
    if (!selectedConnector) return;
    try {
      await apiService.deleteConnector(selectedConnector.id, token);
      toast.success("Conector excluído");
      setDeleteConnectorOpen(false);
      setSelectedConnector(null);
      fetchConnectors();
    } catch (error) {
      toast.error("Erro ao excluir conector");
    }
  };

  const handleViewSchema = async (connector) => {
    setSelectedConnector(connector);
    setSchemaDialogOpen(true);
    setLoadingSchema(true);
    setSchemaData(null);
    try {
      const response = await apiService.introspectConnector(
        connector.id,
        token,
      );
      setSchemaData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar schema");
    } finally {
      setLoadingSchema(false);
    }
  };

  // Tables selection handlers
  const handleOpenTablesDialog = async (connector) => {
    setSelectedConnector(connector);
    setTablesDialogOpen(true);
    setLoadingTables(true);
    setAllTables([]);
    setSelectedTables([]);
    setTableSearchTerm("");

    try {
      const response = await apiService.getConnectorTables(connector.id, token);
      setAllTables(response.data.tables || []);
      setSelectedTables(response.data.selected_tables || []);
    } catch (error) {
      toast.error("Erro ao carregar tabelas");
      setTablesDialogOpen(false);
    } finally {
      setLoadingTables(false);
    }
  };

  const handleToggleTable = (tableName) => {
    setSelectedTables((prev) => {
      if (prev.includes(tableName)) {
        return prev.filter((t) => t !== tableName);
      } else {
        return [...prev, tableName];
      }
    });
  };

  const handleSelectAllTables = () => {
    const filteredTables = allTables.filter((t) =>
      t.toLowerCase().includes(tableSearchTerm.toLowerCase()),
    );
    setSelectedTables((prev) => {
      const allSelected = filteredTables.every((t) => prev.includes(t));
      if (allSelected) {
        return prev.filter((t) => !filteredTables.includes(t));
      } else {
        return [...new Set([...prev, ...filteredTables])];
      }
    });
  };

  const handleSaveTables = async () => {
    if (!selectedConnector) return;
    setSavingTables(true);
    try {
      await apiService.updateConnectorTables(
        selectedConnector.id,
        selectedTables,
        token,
      );
      toast.success(`${selectedTables.length} tabelas selecionadas!`);
      setTablesDialogOpen(false);
      fetchConnectors();
    } catch (error) {
      toast.error("Erro ao salvar tabelas");
    } finally {
      setSavingTables(false);
    }
  };

  const filteredTablesForSelection = allTables.filter((t) =>
    t.toLowerCase().includes(tableSearchTerm.toLowerCase()),
  );

  // Template handlers
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      await apiService.deleteTemplate(selectedTemplate.id, token);
      toast.success("Template excluído");
      setDeleteTemplateOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error("Erro ao excluir template");
    }
  };

  const handleApplyTemplate = async (template) => {
    setApplyingTemplate(template.id);
    try {
      const result = await apiService.applyTemplate(template.id, null, token);
      toast.success(
        `${result.data.type === "dashboard" ? "Dashboard" : "Extração"} criado(a) a partir do template!`,
      );
      fetchTemplates();
    } catch (error) {
      toast.error("Erro ao aplicar template");
    } finally {
      setApplyingTemplate(null);
    }
  };

  const getTemplateTypeLabel = (type) => {
    switch (type) {
      case "extraction_template":
        return "Extração";
      case "dashboard_template":
        return "Dashboard";
      case "widget_template":
        return "Widget";
      default:
        return type;
    }
  };

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            Configurações
          </h1>
          <p className="text-slate-500 mt-1">
            Gerencie conectores, templates e configurações do sistema
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex w-full justify-start overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="connectors"
              className="flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <Database className="w-4 h-4" />
              Conectores
            </TabsTrigger>
            <TabsTrigger
              value="templates"
              className="flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <FileText className="w-4 h-4" />
              Templates
            </TabsTrigger>

            {user?.role === "admin" && (
              <TabsTrigger
                value="users"
                className="flex items-center gap-2 whitespace-nowrap shrink-0"
              >
                <Users className="w-4 h-4" />
                Usuários
              </TabsTrigger>
            )}

            <TabsTrigger
              value="account"
              className="flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <Settings2 className="w-4 h-4" />
              Conta
            </TabsTrigger>
          </TabsList>

          {/* Connectors Tab */}
          <TabsContent value="connectors" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <p className="text-sm text-slate-500">
                Conectores permitem acessar bancos de dados Firebird 3.0
              </p>
              <Button
                onClick={() => handleOpenConnectorDialog()}
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                data-testid="add-connector-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Conector
              </Button>
            </div>

            {loadingConnectors ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
              </div>
            ) : connectors.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Database className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-heading font-medium text-slate-900 mb-2">
                  Nenhum conector
                </h3>
                <p className="text-slate-500 text-sm mb-4">
                  Adicione um conector para começar
                </p>
                <Button
                  onClick={() => handleOpenConnectorDialog()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Conector
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {connectors.map((connector, index) => (
                  <div
                    key={connector.id}
                    className="card p-4 flex flex-col sm:flex-row sm:items-center gap-4 animate-fadeIn"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Database className="w-5 h-5 text-emerald-600" />
                      </div>

                      <div className="flex min-w-0 flex-col">
                        <h3
                          className="font-medium text-slate-900 truncate"
                          data-testid={`connector-name-${connector.id}`}
                        >
                          {connector.name}
                        </h3>
                        <p className="text-sm text-slate-500 truncate">
                          {connector.host}:{connector.port} •{" "}
                          {connector.database_path}
                        </p>
                        {connector.selected_tables?.length > 0 && (
                          <p className="text-xs text-indigo-600 mt-1">
                            <Table className="w-3 h-3 inline mr-1" />
                            {connector.selected_tables.length} tabelas
                            selecionadas
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-3 flex-wrap w-full sm:w-auto mt-2 sm:mt-0 pt-3 sm:pt-0 border-t border-slate-100 sm:border-0">
                      {testResult?.id === connector.id && (
                        <div
                          className={`flex items-center gap-1 text-sm ${testResult.success ? "text-emerald-600" : "text-red-600"}`}
                        >
                          {testResult.success ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          {testResult.success ? "Conectado" : "Falhou"}
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestConnector(connector.id)}
                          disabled={testingConnector === connector.id}
                          data-testid={`test-connector-${connector.id}`}
                          title="Testar conexão"
                        >
                          {testingConnector === connector.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <TestTube className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenTablesDialog(connector)}
                          data-testid={`config-tables-${connector.id}`}
                          title="Configurar tabelas"
                        >
                          <Table className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSchema(connector)}
                          data-testid={`view-schema-${connector.id}`}
                          title="Ver schema"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-slate-100 rounded-lg shrink-0">
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                handleOpenConnectorDialog(connector)
                              }
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedConnector(connector);
                                setDeleteConnectorOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {user?.role === "admin" && (
            <TabsContent
              value="users"
              className="animate-in fade-in slide-in-from-bottom-4 duration-500"
            >
              <UsersManagementTab />
            </TabsContent>
          )}

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <p className="text-sm text-slate-500">
              Templates permitem reutilizar configurações de extrações e
              dashboards
            </p>

            {loadingTemplates ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-heading font-medium text-slate-900 mb-2">
                  Nenhum template
                </h3>
                <p className="text-slate-500 text-sm">
                  Templates serão criados automaticamente ou pelo WILL
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {templates.map((template, index) => (
                  <div
                    key={template.id}
                    className="card p-4 animate-fadeIn"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-violet-600" />
                      </div>
                      <span className="badge badge-info">
                        {getTemplateTypeLabel(template.type)}
                      </span>
                    </div>

                    <h3 className="font-medium text-slate-900 mb-1">
                      {template.name}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Criado em{" "}
                      {new Date(template.created_at).toLocaleDateString(
                        "pt-BR",
                      )}
                    </p>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleApplyTemplate(template)}
                        disabled={applyingTemplate === template.id}
                      >
                        {applyingTemplate === template.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Copy className="w-4 h-4 mr-1" />
                        )}
                        Aplicar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setDeleteTemplateOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            <div className="card p-6">
              <h3 className="font-heading font-semibold text-slate-900 mb-4">
                Informações da Conta
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-500">Nome</Label>
                  <p className="font-medium text-slate-900">
                    {user?.name || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">E-mail</Label>
                  <p className="font-medium text-slate-900">
                    {user?.email || "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Perfil</Label>
                  <p className="font-medium text-slate-900 capitalize">
                    {user?.role === "admin"
                      ? "Administrador"
                      : "Usuário Padrão"}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">IA WILL</Label>
                  <p className="font-medium text-slate-900">
                    {user?.ai_enabled ? "Habilitado" : "Desabilitado"}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Connector Dialog */}
        <Dialog
          open={connectorDialogOpen}
          onOpenChange={setConnectorDialogOpen}
        >
          <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingConnector ? "Editar Conector" : "Novo Conector"}
              </DialogTitle>
              <DialogDescription>
                Configure a conexão com o banco de dados Firebird
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  placeholder="Ex: ERP Produção"
                  value={connectorForm.name}
                  onChange={(e) =>
                    setConnectorForm({ ...connectorForm, name: e.target.value })
                  }
                  data-testid="connector-name-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2 space-y-2">
                  <Label>Host *</Label>
                  <Input
                    placeholder="192.168.1.100"
                    value={connectorForm.host}
                    onChange={(e) =>
                      setConnectorForm({
                        ...connectorForm,
                        host: e.target.value,
                      })
                    }
                    data-testid="connector-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Porta</Label>
                  <Input
                    type="number"
                    value={connectorForm.port}
                    onChange={(e) =>
                      setConnectorForm({
                        ...connectorForm,
                        port: parseInt(e.target.value) || 3050,
                      })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Caminho do Banco *</Label>
                <Input
                  placeholder="/dados/erp.fdb"
                  value={connectorForm.database_path}
                  onChange={(e) =>
                    setConnectorForm({
                      ...connectorForm,
                      database_path: e.target.value,
                    })
                  }
                  data-testid="connector-path-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Usuário *</Label>
                  <Input
                    placeholder="SYSDBA"
                    value={connectorForm.username}
                    onChange={(e) =>
                      setConnectorForm({
                        ...connectorForm,
                        username: e.target.value,
                      })
                    }
                    data-testid="connector-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Senha {!editingConnector && "*"}</Label>
                  <Input
                    type="password"
                    placeholder={editingConnector ? "(não alterada)" : "••••••"}
                    value={connectorForm.password}
                    onChange={(e) =>
                      setConnectorForm({
                        ...connectorForm,
                        password: e.target.value,
                      })
                    }
                    data-testid="connector-password-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 mt-2">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setConnectorDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveConnector}
                disabled={savingConnector}
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                data-testid="save-connector-btn"
              >
                {savingConnector && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {editingConnector ? "Atualizar" : "Criar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Connector Dialog */}
        <Dialog
          open={deleteConnectorOpen}
          onOpenChange={setDeleteConnectorOpen}
        >
          <DialogContent className="w-[95vw] sm:w-full max-w-md">
            <DialogHeader>
              <DialogTitle>Excluir Conector</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir "{selectedConnector?.name}"?
                Extrações que usam este conector deixarão de funcionar.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0 mt-4">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setDeleteConnectorOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={handleDeleteConnector}
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Template Dialog */}
        <Dialog open={deleteTemplateOpen} onOpenChange={setDeleteTemplateOpen}>
          <DialogContent className="w-[95vw] sm:w-full max-w-md">
            <DialogHeader>
              <DialogTitle>Excluir Template</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir "{selectedTemplate?.name}"?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0 mt-4">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setDeleteTemplateOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={handleDeleteTemplate}
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schema Dialog */}
        <Dialog open={schemaDialogOpen} onOpenChange={setSchemaDialogOpen}>
          <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6 overflow-hidden">
            <DialogHeader className="shrink-0 mb-2">
              <DialogTitle>Schema: {selectedConnector?.name}</DialogTitle>
              <DialogDescription>Estrutura do banco de dados</DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0 pr-1">
              {loadingSchema ? (
                <div className="flex items-center justify-center py-10 h-full">
                  <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                </div>
              ) : schemaData ? (
                <div className="space-y-4 pb-2">
                  {schemaData.tables?.map((table) => (
                    <div
                      key={table.name}
                      className="border border-slate-200 rounded-lg overflow-hidden flex flex-col"
                    >
                      <div className="bg-slate-50 px-4 py-2 font-medium text-slate-900 border-b border-slate-200 shrink-0">
                        {table.name}
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50/50">
                              <th className="px-4 py-2 text-left font-medium text-slate-600 whitespace-nowrap">
                                Coluna
                              </th>
                              <th className="px-4 py-2 text-left font-medium text-slate-600 whitespace-nowrap">
                                Tipo
                              </th>
                              <th className="px-4 py-2 text-left font-medium text-slate-600 whitespace-nowrap">
                                Nulo
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {table.columns.map((col) => (
                              <tr
                                key={col.name}
                                className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors"
                              >
                                <td className="px-4 py-2 font-mono text-sm whitespace-nowrap">
                                  {col.name}
                                </td>
                                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                                  {col.type}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {col.nullable ? (
                                    <span className="text-slate-400">Sim</span>
                                  ) : (
                                    <span className="text-slate-900 font-medium">
                                      Não
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-slate-500 text-center py-10">
                    Schema não disponível
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Tables Selection Dialog */}
        <Dialog open={tablesDialogOpen} onOpenChange={setTablesDialogOpen}>
          <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6 overflow-hidden">
            <DialogHeader className="shrink-0 mb-2">
              <DialogTitle>
                Configurar Tabelas: {selectedConnector?.name}
              </DialogTitle>
              <DialogDescription>
                Selecione as tabelas que deseja trabalhar. Apenas estas tabelas
                aparecerão nas extrações e no WILL.
              </DialogDescription>
            </DialogHeader>

            {loadingTables ? (
              <div className="flex flex-1 items-center justify-center py-16">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-500">
                    Carregando tabelas do banco...
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Search and Select All */}
                <div className="shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 py-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar tabelas..."
                      value={tableSearchTerm}
                      onChange={(e) => setTableSearchTerm(e.target.value)}
                      className="pl-10 w-full"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={handleSelectAllTables}
                  >
                    {filteredTablesForSelection.every((t) =>
                      selectedTables.includes(t),
                    )
                      ? "Desmarcar"
                      : "Selecionar"}{" "}
                    filtrados
                  </Button>
                </div>

                {/* Stats */}
                <div className="shrink-0 flex flex-wrap items-center gap-2 text-sm text-slate-500 pb-2 border-b">
                  <span>{allTables.length} tabelas no banco</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="text-indigo-600 font-medium">
                    {selectedTables.length} selecionadas
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg mt-2 min-h-0 bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 p-2">
                    {filteredTablesForSelection.map((tableName) => (
                      <div
                        key={tableName}
                        onClick={() => handleToggleTable(tableName)}
                        className={`
                          flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors select-none
                          ${
                            selectedTables.includes(tableName)
                              ? "bg-indigo-50 border border-indigo-200"
                              : "hover:bg-slate-50 border border-transparent"
                          }
                        `}
                        data-testid={`table-checkbox-${tableName}`}
                      >
                        <div
                          className={`
                          w-5 h-5 rounded flex items-center justify-center border transition-colors flex-shrink-0
                          ${
                            selectedTables.includes(tableName)
                              ? "bg-indigo-600 border-indigo-600"
                              : "border-slate-300"
                          }
                        `}
                        >
                          {selectedTables.includes(tableName) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span
                          className={`text-sm truncate w-full ${selectedTables.includes(tableName) ? "text-indigo-900 font-medium" : "text-slate-700"}`}
                          title={tableName}
                        >
                          {tableName}
                        </span>
                      </div>
                    ))}
                  </div>

                  {filteredTablesForSelection.length === 0 && (
                    <p className="text-center text-slate-400 py-8">
                      Nenhuma tabela encontrada
                    </p>
                  )}
                </div>
              </>
            )}

            <DialogFooter className="shrink-0 mt-4 gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setTablesDialogOpen(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveTables}
                disabled={savingTables || loadingTables}
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
              >
                {savingTables && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Salvar ({selectedTables.length} tabelas)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SettingsPage;
